import React from 'react';

import { usePipelineStages } from '../services/usePipelineStages';

export interface StudentProgressProps {
  currentStage: string;
  className?: string;
  useBusinessTerms?: boolean;
  onStageSelect?: (stageCode: string) => void;
  selectedStage?: string;
}

// Helper to map backend API data to the frontend UI (add color, etc.)
const STAGE_COLORS: Record<string, string> = {
  visitor: 'bg-gray-400',
  prospect: 'bg-blue-400',
  applicant: 'bg-yellow-400',
  orientation: 'bg-green-400',
  pre_departure: 'bg-indigo-400',
  active_student: 'bg-purple-400',
  alumni: 'bg-pink-400',
};



export const StudentProgress: React.FC<StudentProgressProps> = ({
  currentStage,
  className = '',
  useBusinessTerms = false,
  onStageSelect,
  selectedStage,
}) => {
  const [pipelineStages] = usePipelineStages();
  // pipelineStages now contains all metadata from the backend
  const stages = pipelineStages.map((stage: any) => ({
    ...stage,
    color: STAGE_COLORS[stage.code] || 'bg-gray-400',
  }));
  const stageIndex = stages.findIndex(s => s.code === currentStage);
  const selectedStageCode = selectedStage || currentStage;
  const currentStageLabel = stageIndex >= 0 ? `Stage ${stageIndex + 1} of ${stages.length}` : '';
  const progressRatio = stages.length > 1 ? Math.max(stageIndex, 0) / (stages.length - 1) : 0;
  const nextStage = stageIndex >= 0 ? stages[stageIndex + 1] : null;
  void nextStage;

  return (
    <div className={`glass-panel bg-white/85 dark:bg-slate-900/70 border border-slate-100 dark:border-slate-700 rounded-2xl shadow-xl overflow-hidden ${className}`}>
      <div className="px-4 py-3 sm:px-6 sm:py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
        <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">Pathway Progress</h3>
        {currentStageLabel && (
          <span className="text-[10px] sm:text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400">{currentStageLabel}</span>
        )}
      </div>
      <div className="block sm:hidden px-4 py-4">
        <div className="mt-3 h-1.5 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
          <div
            className="h-full bg-emerald-500 transition-[width] duration-500"
            style={{ width: `${Math.round(progressRatio * 100)}%` }}
          />
        </div>
      </div>
      <div className="hidden sm:block px-6 py-8">
        <div className="relative">
          <div className="absolute top-5 left-8 right-8 h-0.5 bg-slate-200 dark:bg-slate-700"></div>
          <div
            className="absolute top-5 left-8 h-0.5 bg-emerald-500 transition-all duration-500"
            style={{ width: `${Math.round(progressRatio * 100)}%` }}
          ></div>
          <div className="relative flex justify-between px-2">
            {stages.map((stage, index) => {
              const isActive = currentStage === stage.code;
              const isCompleted = stageIndex > index;
              const isSelected = selectedStageCode === stage.code;
              const canSelect = Boolean(onStageSelect) && (isCompleted || isActive);
              const circle = (
                <div
                  className={`relative flex flex-col items-center text-center ${canSelect ? 'cursor-pointer focus:outline-none transition transform hover:-translate-y-1 hover:scale-[1.02]' : ''}`}
                >
                  <div
                    className={`relative w-12 h-12 rounded-full flex items-center justify-center text-base font-semibold border-2 transition-all duration-300 ${
                      isActive
                        ? 'bg-blue-500 text-white border-blue-600'
                        : isCompleted
                          ? 'bg-emerald-500 text-white border-emerald-600'
                          : 'bg-white text-slate-400 border-slate-300 dark:bg-slate-900 dark:text-slate-500 dark:border-slate-600'
                    } ${isSelected ? 'ring-4 ring-emerald-200 dark:ring-emerald-700/50' : ''}`}
                  >
                    <span className="relative z-10">{index + 1}</span>
                  </div>
                  <span className={`mt-2 text-xs font-medium ${isCompleted || isActive ? 'text-slate-900 dark:text-white' : 'text-slate-500 dark:text-slate-400'}`}>
                    {useBusinessTerms ? (stage.businessTitle || stage.title) : stage.title}
                  </span>
                </div>
              );
              return (
                <div key={stage.code} className="flex-1 flex justify-center">
                  {canSelect ? (
                    <button type="button" onClick={() => onStageSelect && onStageSelect(stage.code)} className="focus:outline-none">
                      {circle}
                    </button>
                  ) : (
                    circle
                  )}
                </div>
              );
            })}
          </div>
        </div>
        
      </div>
    </div>
  );
};
